import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
  XMarkIcon,
  PhoneIcon,
  MapPinIcon,
  GlobeAltIcon,
  StarIcon,
  EyeIcon,
  PaperAirplaneIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { getFirestore, collection, query, getDocs, doc, updateDoc, orderBy, where, writeBatch, addDoc } from 'firebase/firestore';

const ManualBusinessList = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBusinesses, setSelectedBusinesses] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [bulkSelectionCount, setBulkSelectionCount] = useState(50);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    templateTitle: '',
    templateDigimileName: '',
    templateDescription: '',
    language: 'en'
  });
  const [isAddingTemplate, setIsAddingTemplate] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);

  // Fetch templates from Firebase
  const fetchTemplates = async () => {
    try {
      const db = getFirestore();
      const templatesRef = collection(db, 'WhatsappTemplates');
      const q = query(templatesRef, orderBy('createdDate', 'desc'));
      const querySnapshot = await getDocs(q);
      const templatesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAvailableTemplates(templatesData);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  // Fetch businesses from Firebase
  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const businessesRef = collection(db, 'ManualUserList');
      
      // Create query with sorting
      const q = query(
        businessesRef,
        orderBy(sortField, sortDirection)
      );
      
      const querySnapshot = await getDocs(q);
      const businessesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setBusinesses(businessesData);
      setError(null);
    } catch (err) {
      console.error('Error fetching businesses:', err);
      setError('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinesses();
    fetchTemplates(); // Fetch templates when the component mounts
  }, [sortField, sortDirection]);

  // Filter businesses based on search term
  const filteredBusinesses = businesses.filter(business =>
    (business.name && typeof business.name === 'string' && business.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (business.category && typeof business.category === 'string' && business.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (business.phone_number && business.phone_number.includes(searchTerm)) ||
    (business.address_1 && typeof business.address_1 === 'string' && business.address_1.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (business.address_2 && typeof business.address_2 === 'string' && business.address_2.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredBusinesses.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentBusinesses = filteredBusinesses.slice(startIndex, endIndex);

  // Handle search
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setSelectedBusinesses(new Set());
  };

  // Handle bulk selection
  const handleBulkSelection = (count) => {
    setBulkSelectionCount(count);
  };

  const handleSelectFirstN = () => {
    const firstN = filteredBusinesses.slice(0, bulkSelectionCount);
    const newSelected = new Set(firstN.map(business => business.id));
    setSelectedBusinesses(newSelected);
  };

  const handleSelectAllOnCurrentPage = () => {
    const newSelected = new Set([...selectedBusinesses, ...currentBusinesses.map(business => business.id)]);
    setSelectedBusinesses(newSelected);
  };

  const handleClearSelection = () => {
    setSelectedBusinesses(new Set());
  };

  // Handle individual selection
  const handleSelectBusiness = (businessId) => {
    const newSelected = new Set(selectedBusinesses);
    if (newSelected.has(businessId)) {
      newSelected.delete(businessId);
    } else {
      newSelected.add(businessId);
    }
    setSelectedBusinesses(newSelected);
  };

  // Handle select all on current page
  const handleSelectAllCurrentPage = () => {
    const currentPageIds = currentBusinesses.map(business => business.id);
    const allSelected = currentPageIds.every(id => selectedBusinesses.has(id));
    
    if (allSelected) {
      // Deselect all on current page
      const newSelected = new Set(selectedBusinesses);
      currentPageIds.forEach(id => newSelected.delete(id));
      setSelectedBusinesses(newSelected);
    } else {
      // Select all on current page
      const newSelected = new Set([...selectedBusinesses, ...currentPageIds]);
      setSelectedBusinesses(newSelected);
    }
  };

  // Handle send referral toggle
  const handleToggleSendReferral = async (businessId, currentValue) => {
    try {
      const db = getFirestore();
      const businessRef = doc(db, 'ManualUserList', businessId);
      
      await updateDoc(businessRef, {
        sendReferral: !currentValue,
        updatedAt: new Date()
      });

      // Update local state
      setBusinesses(prev => prev.map(business => 
        business.id === businessId 
          ? { ...business, sendReferral: !currentValue, updatedAt: new Date() }
          : business
      ));

      console.log(`Updated sendReferral for business ${businessId} to ${!currentValue}`);
      
      // Show success message
      if (!currentValue) {
        alert('✅ WhatsApp referral enabled! The cloud function will automatically send the message.');
      } else {
        alert('❌ WhatsApp referral disabled.');
      }

    } catch (error) {
      console.error('Error updating sendReferral:', error);
      alert('Error updating referral status. Please try again.');
    }
  };

  // Handle bulk WhatsApp sending
  const handleBulkWhatsAppSend = async () => {
    if (selectedBusinesses.size === 0) {
      alert('Please select at least one business to send WhatsApp messages.');
      return;
    }

    setShowTemplateModal(true);
  };

  // Handle template selection and sending
  const handleTemplateSend = async () => {
    if (!selectedTemplate) {
      alert('Please select a template.');
      return;
    }

    setIsSending(true);
    try {
      const db = getFirestore();
      const batch = writeBatch(db);
      
      // Get selected business IDs
      const selectedIds = Array.from(selectedBusinesses);
      
      // Find the selected template to get its templateDigimileName
      const selectedTemplateData = availableTemplates.find(t => t.id === selectedTemplate);
      if (!selectedTemplateData) {
        alert('Selected template not found. Please try again.');
        setIsSending(false);
        return;
      }
      
      // Update each selected business with templateDigimileName and sendReferral flag
      selectedIds.forEach(businessId => {
        const businessRef = doc(db, 'ManualUserList', businessId);
        batch.update(businessRef, {
          template: selectedTemplateData.templateDigimileName, // Store templateDigimileName instead of doc ID
          sendReferral: true,
          lastWhatsAppSent: new Date(),
          updatedAt: new Date()
        });
      });

      await batch.commit();

      // Update local state
      setBusinesses(prev => prev.map(business => 
        selectedBusinesses.has(business.id)
          ? { 
              ...business, 
              template: selectedTemplateData.templateDigimileName, // Update local state with templateDigimileName
              sendReferral: true, 
              lastWhatsAppSent: new Date(),
              updatedAt: new Date() 
            }
          : business
      ));

      // Clear selection
      setSelectedBusinesses(new Set());
      setShowTemplateModal(false);
      setSelectedTemplate('');

      alert(`✅ Successfully updated ${selectedIds.length} businesses with template "${selectedTemplateData.templateTitle}". The cloud function will automatically send WhatsApp messages.`);

    } catch (error) {
      console.error('Error updating businesses:', error);
      alert('Error updating businesses. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  // Handle adding new template
  const handleAddTemplate = async () => {
    if (!newTemplate.templateTitle || !newTemplate.templateDigimileName) {
      alert('Please fill in all required fields (Template Title and Template Digimile Name).');
      return;
    }

    setIsAddingTemplate(true);
    try {
      const db = getFirestore();
      const templatesRef = collection(db, 'WhatsappTemplates');
      
      const templateData = {
        ...newTemplate,
        createdDate: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(templatesRef, templateData);
      console.log('Template saved with ID:', docRef.id);

      // Refresh templates list
      await fetchTemplates();

      // Reset form
      setNewTemplate({
        templateTitle: '',
        templateDigimileName: '',
        templateDescription: '',
        language: 'en'
      });

      setShowAddTemplateModal(false);
      alert('✅ Template added successfully!');

    } catch (error) {
      console.error('Error adding template:', error);
      alert('Error adding template. Please try again.');
    } finally {
      setIsAddingTemplate(false);
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get selection info
  const getSelectionInfo = () => {
    const total = businesses.length;
    const selected = selectedBusinesses.size;
    const currentPageSelected = currentBusinesses.filter(business => 
      selectedBusinesses.has(business.id)
    ).length;
    
    return { total, selected, currentPageSelected };
  };

  const selectionInfo = getSelectionInfo();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchBusinesses}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <h1 className="text-2xl font-semibold text-gray-900">Business List & WhatsApp</h1>
            </div>
            <div className="text-sm text-gray-500">
              {businesses.length} businesses total
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowAddTemplateModal(true)}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center space-x-1"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Manage Templates</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6 py-6">
        {/* Search and Bulk Selection */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search businesses..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Bulk Actions */}
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
              {/* Bulk Selection Dropdown */}
              <div className="relative">
                <select
                  value={bulkSelectionCount}
                  onChange={(e) => handleBulkSelection(Number(e.target.value))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={50}>First 50</option>
                  <option value={100}>First 100</option>
                  <option value={200}>First 200</option>
                  <option value={500}>First 500</option>
                  <option value={900}>First 900</option>
                  <option value={filteredBusinesses.length}>All ({filteredBusinesses.length})</option>
                </select>
              </div>

              <button
                onClick={handleSelectFirstN}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
              >
                Select
              </button>

              <button
                onClick={handleSelectAllOnCurrentPage}
                className="px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
              >
                Select All on Page
              </button>

              <button
                onClick={handleClearSelection}
                className="px-3 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
              >
                Clear Selection
              </button>
            </div>
          </div>

          {/* Selection Info */}
          {selectionInfo.selected > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center justify-between">
                <p className="text-sm text-blue-800">
                  <strong>{selectionInfo.selected}</strong> of <strong>{selectionInfo.total}</strong> businesses selected
                  {selectionInfo.currentPageSelected > 0 && (
                    <span className="ml-2 text-blue-600">
                      ({selectionInfo.currentPageSelected} on current page)
                    </span>
                  )}
                </p>
                <button
                  onClick={handleBulkWhatsAppSend}
                  className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center space-x-2"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                  <span>Send WhatsApp</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Businesses Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={currentBusinesses.length > 0 && currentBusinesses.every(business => selectedBusinesses.has(business.id))}
                      onChange={handleSelectAllCurrentPage}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Business Name</span>
                      {sortField === 'name' && (
                        sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('category')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Category</span>
                      {sortField === 'category' && (
                        sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('city')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>City</span>
                      {sortField === 'city' && (
                        sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('locality')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Locality</span>
                      {sortField === 'locality' && (
                        sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact Info
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rating
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last WhatsApp Sent
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentBusinesses.map((business) => (
                  <tr key={business.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedBusinesses.has(business.id)}
                        onChange={() => handleSelectBusiness(business.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{business.name}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{business.category || '-'}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{business.city || '-'}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{business.locality || '-'}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <div className="flex items-center text-sm text-gray-900">
                          <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                          {business.phone_number}
                        </div>
                        {business.website && (
                          <div className="flex items-center text-sm text-blue-600">
                            <GlobeAltIcon className="h-4 w-4 mr-1" />
                            <a href={business.website} target="_blank" rel="noopener noreferrer" className="hover:underline">
                              Website
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {business.rating ? (
                        <div className="flex items-center">
                          <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                          <span className="text-sm text-gray-900">{business.rating}</span>
                          {business.reviews_count && (
                            <span className="text-sm text-gray-500 ml-1">({business.reviews_count})</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {business.lastWhatsAppSent ? (
                        new Date(business.lastWhatsAppSent).toLocaleString()
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedBusiness(business);
                            setShowModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 font-medium"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(endIndex, filteredBusinesses.length)}</span> of{' '}
                    <span className="font-medium">{filteredBusinesses.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Business Details Modal */}
      {showModal && selectedBusiness && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Business Details</h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedBusiness(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Business Name</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedBusiness.name}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Category</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedBusiness.category || '-'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                    <p className="mt-1 text-sm text-gray-900 flex items-center">
                      <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                      {selectedBusiness.phone_number}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Website</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedBusiness.website ? (
                        <a 
                          href={selectedBusiness.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          <GlobeAltIcon className="h-4 w-4 mr-1" />
                          {selectedBusiness.website}
                        </a>
                      ) : (
                        '-'
                      )}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Rating</label>
                    <p className="mt-1 text-sm text-gray-900 flex items-center">
                      {selectedBusiness.rating ? (
                        <>
                          <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                          {selectedBusiness.rating}
                          {selectedBusiness.reviews_count && (
                            <span className="text-gray-500 ml-1">({selectedBusiness.reviews_count} reviews)</span>
                          )}
                        </>
                      ) : (
                        '-'
                      )}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">WhatsApp Status</label>
                    <p className="mt-1 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        selectedBusiness.sendReferral
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedBusiness.sendReferral ? (
                          <>
                            <CheckIcon className="h-3 w-3 mr-1" />
                            Enabled
                          </>
                        ) : (
                          <>
                            <XMarkIcon className="h-3 w-3 mr-1" />
                            Disabled
                          </>
                        )}
                      </span>
                    </p>
                  </div>
                  
                  {selectedBusiness.template && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Template</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {availableTemplates.find(t => t.id === selectedBusiness.template)?.templateTitle || selectedBusiness.template}
                      </p>
                    </div>
                  )}
                  
                  {selectedBusiness.lastWhatsAppSent && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last WhatsApp Sent</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {new Date(selectedBusiness.lastWhatsAppSent).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Complete Address</label>
                  <p className="mt-1 text-sm text-gray-900 flex items-start">
                    <MapPinIcon className="h-4 w-4 mr-1 mt-0.5 text-gray-400 flex-shrink-0" />
                    <div>
                      {selectedBusiness.address_full ? (
                        selectedBusiness.address_full
                      ) : (
                        <>
                          {selectedBusiness.address_1 && <div>{selectedBusiness.address_1}</div>}
                          {selectedBusiness.address_2 && <div className="text-gray-500">{selectedBusiness.address_2}</div>}
                          {!selectedBusiness.address_1 && !selectedBusiness.address_2 && <span className="text-gray-400">-</span>}
                        </>
                      )}
                    </div>
                  </p>
                </div>
                
                {/* Location Components */}
                {(selectedBusiness.locality || selectedBusiness.city || selectedBusiness.state || selectedBusiness.pinCode || selectedBusiness.country) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedBusiness.locality && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Locality</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedBusiness.locality}</p>
                      </div>
                    )}
                    
                    {selectedBusiness.city && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">City</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedBusiness.city}</p>
                      </div>
                    )}
                    
                    {selectedBusiness.state && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">State</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedBusiness.state}</p>
                      </div>
                    )}
                    
                    {selectedBusiness.pinCode && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Pin Code</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedBusiness.pinCode}</p>
                      </div>
                    )}
                    
                    {selectedBusiness.country && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Country</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedBusiness.country}</p>
                      </div>
                    )}
                  </div>
                )}
                
                {selectedBusiness.image_source && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Image Source</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedBusiness.image_source}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Created At</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedBusiness.createdAt ? 
                        (selectedBusiness.createdAt.toDate ? 
                          new Date(selectedBusiness.createdAt.toDate()).toLocaleString() : 
                          new Date(selectedBusiness.createdAt).toLocaleString()
                        ) : '-'
                      }
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedBusiness.updatedAt ? 
                        (selectedBusiness.updatedAt.toDate ? 
                          new Date(selectedBusiness.updatedAt.toDate()).toLocaleString() : 
                          new Date(selectedBusiness.updatedAt).toLocaleString()
                        ) : '-'
                      }
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedBusiness(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Selection Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Select WhatsApp Template</h3>
                <button
                  onClick={() => {
                    setShowTemplateModal(false);
                    setSelectedTemplate('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  You have selected <strong>{selectedBusinesses.size}</strong> business(es). Choose a template to send WhatsApp messages.
                </p>
                
                <div className="space-y-3">
                  {availableTemplates.length > 0 ? (
                    availableTemplates.map((template) => (
                      <div
                        key={template.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedTemplate === template.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedTemplate(template.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{template.templateTitle}</h4>
                            <p className="text-sm text-gray-600">{template.templateDescription}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Digimile: {template.templateDigimileName} | Language: {template.language}
                            </p>
                          </div>
                          {selectedTemplate === template.id && (
                            <CheckIcon className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                      </div>
                    ))
                                     ) : (
                     <div className="text-center py-8 text-gray-500">
                       <p className="mb-4">No templates available. Click "Add Template" to create your first template.</p>
                       <button
                         onClick={() => {
                           setShowTemplateModal(false); // Close selection modal
                           setShowAddTemplateModal(true); // Open add template modal
                         }}
                         className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center space-x-2 mx-auto"
                       >
                         <PlusIcon className="h-4 w-4" />
                         <span>Add New Template</span>
                       </button>
                     </div>
                   )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowTemplateModal(false);
                    setSelectedTemplate('');
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTemplateSend}
                  disabled={!selectedTemplate || isSending}
                  className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isSending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="h-4 w-4" />
                      <span>Send WhatsApp Messages</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Template Modal */}
      {showAddTemplateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add New WhatsApp Template</h3>
                <button
                  onClick={() => {
                    setShowAddTemplateModal(false);
                    setNewTemplate({
                      templateTitle: '',
                      templateDigimileName: '',
                      templateDescription: '',
                      language: 'en'
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Title *
                  </label>
                  <input
                    type="text"
                    value={newTemplate.templateTitle}
                    onChange={(e) => setNewTemplate({ ...newTemplate, templateTitle: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter template title"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Digimile Name *
                  </label>
                  <input
                    type="text"
                    value={newTemplate.templateDigimileName}
                    onChange={(e) => setNewTemplate({ ...newTemplate, templateDigimileName: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter digimile name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Description
                  </label>
                  <textarea
                    value={newTemplate.templateDescription}
                    onChange={(e) => setNewTemplate({ ...newTemplate, templateDescription: e.target.value })}
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter template description"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Language
                  </label>
                  <select
                    value={newTemplate.language}
                    onChange={(e) => setNewTemplate({ ...newTemplate, language: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                    <option value="mr">Marathi</option>
                    <option value="gu">Gujarati</option>
                    <option value="bn">Bengali</option>
                    <option value="ta">Tamil</option>
                    <option value="te">Telugu</option>
                    <option value="kn">Kannada</option>
                    <option value="ml">Malayalam</option>
                    <option value="pa">Punjabi</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddTemplateModal(false);
                    setNewTemplate({
                      templateTitle: '',
                      templateDigimileName: '',
                      templateDescription: '',
                      language: 'en'
                    });
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddTemplate}
                  disabled={isAddingTemplate || !newTemplate.templateTitle || !newTemplate.templateDigimileName}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isAddingTemplate ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <PlusIcon className="h-4 w-4" />
                      <span>Add Template</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualBusinessList;
